import Phaser from '../lib/phaser.js';
import { Controls } from '../utils/controls.js';
import { SCENE_KEYS } from './scene-keys.js';

/** HUD overlays that run on top of the world. They must not intercept clicks on a full-screen
 *  overlay menu (Quests/Map/Settings/etc.), so overlay scenes sleep them while open. */
const HUD_SCENE_KEYS = [SCENE_KEYS.WORLD_HUD_SCENE, SCENE_KEYS.CURRENCY_HUD_SCENE];

/** Background scenes that stay active (paused, not stopped) under an overlay and still keep their
 *  own InputPlugin live. Phaser dispatches pointer events to EVERY active scene's input plugin, so
 *  a still-listening lower scene (the world's NPC/sign hit-zones, or the always-launched Dialog /
 *  Cutscene scenes) can win a cross-scene hit-test and swallow the overlay's CLOSE/DONE pointerup.
 *  Overlay scenes disable input on these while open and restore it on shutdown. */
const INPUT_MUTE_SCENE_KEYS = [SCENE_KEYS.WORLD_SCENE, SCENE_KEYS.DIALOG_SCENE, SCENE_KEYS.CUTSCENE_SCENE];

export class BaseScene extends Phaser.Scene {
  /** @protected @type {Controls} */
  _controls;

  /**
   * @param {string | Phaser.Types.Scenes.SettingsConfig} [config]
   */
  constructor(config) {
    super(config);
    if (this.constructor === BaseScene) {
      throw new Error('BaseScene is an abstract class and cannot be instantiated.');
    }
  }

  /**
   * @param {any | undefined} [data]
   * @returns {void}
   */
  init(data) {
    if (data) {
      this._log(`[${this.constructor.name}:init] invoked, data provided: ${JSON.stringify(data)}`);
      return;
    }
    this._log(`[${this.constructor.name}:init] invoked`);
  }

  /**
   * @returns {void}
   */
  preload() {
    this._log(`[${this.constructor.name}:preload] invoked`);
  }

  /**
   * @returns {void}
   */
  create() {
    this._log(`[${this.constructor.name}:create] invoked`);

    this._controls = new Controls(this);
    this.events.on(Phaser.Scenes.Events.RESUME, this.handleSceneResume, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.handleSceneCleanup, this);

    this.scene.bringToTop();
  }

  /**
   * @param {DOMHighResTimeStamp} [time]
   * @returns {void}
   */
  update(time) {
    if (this._controls === undefined) {
      return;
    }

    // Ignore game keys (e.g. F = fullscreen) while the user is typing in a DOM input/textarea,
    // otherwise typing a name like "Felix" toggles fullscreen / the box eats keys oddly.
    const ae = typeof document !== 'undefined' ? document.activeElement : null;
    if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable)) {
      return;
    }

    if (!this._controls.wasFKeyPressed()) {
      return;
    }

    if (this.scale.isFullscreen) {
      this.scale.stopFullscreen();
      return;
    }

    this.scale.startFullscreen();
  }

  /**
   * @param {Phaser.Scenes.Systems} sys
   * @param {any | undefined} [data]
   * @returns {void}
   */
  handleSceneResume(sys, data) {
    this._controls.lockInput = false;
    if (data) {
      this._log(`[${this.constructor.name}:handleSceneResume] invoked, data provided: ${JSON.stringify(data)}`);
      return;
    }
    this._log(`[${this.constructor.name}:handleSceneResume] invoked`);
  }

  handleSceneCleanup() {
    this._log(`[${this.constructor.name}:handleSceneCleanup] invoked`);
    this.events.off(Phaser.Scenes.Events.RESUME, this.handleSceneResume, this);
  }

  /**
   * Sleep the always-on HUD overlays while this full-screen overlay menu is open, and wake them
   * again when it shuts down. Phaser's input is globally top-only across scenes, so a still-active
   * HUD's interactive icons can otherwise swallow clicks (e.g. CLOSE) on the overlay. Bring this
   * scene to the top so its buttons sit above everything. Idempotent + auto-restores on shutdown.
   * @protected
   * @returns {void}
   */
  _claimOverlayInput() {
    const woke = [];
    HUD_SCENE_KEYS.forEach((key) => {
      if (this.scene.isActive(key)) {
        this.scene.sleep(key);
        woke.push(key);
      }
    });

    // Mute (don't sleep — they must keep rendering) the lower background scenes' input plugins so
    // none of them can win a cross-scene pointer hit-test and eat this overlay's CLOSE/DONE press.
    const muted = [];
    INPUT_MUTE_SCENE_KEYS.forEach((key) => {
      const scene = this.scene.get(key);
      if (scene && scene.sys.isActive() && scene.input?.enabled) {
        scene.input.enabled = false;
        muted.push(key);
      }
    });

    // This overlay must be the sole, top-most input target: bring it to the front and make its own
    // input plugin evaluate ALL its interactive objects (not just the single top one) so the close
    // button always resolves even when it sits over the dim backdrop / panel graphics.
    this.scene.bringToTop();
    if (this.input) {
      this.input.topOnly = false;
    }

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      woke.forEach((key) => {
        if (this.scene.isSleeping(key)) {
          this.scene.wake(key);
          this.scene.bringToTop(key);
        }
      });
      muted.forEach((key) => {
        const scene = this.scene.get(key);
        if (scene?.input) {
          scene.input.enabled = true;
        }
      });
    });
  }

  /**
   * @protected
   * @param {string} message
   */
  _log(message) {
    console.log(`%c${message}`, 'color: orange; background: black;');
  }
}
